import { useMemo } from 'react';

import { useGlobalInfo, CLEAR_GROUP_INFO } from '@/hooks/context/useGlobalInfo';

import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';
import useSocialProfiles from '@/hooks/swr/useSocialProfiles';

//utils
import { updateActiveItem } from '@/utils/menu/updateActiveItem';
import { getMenuItemsOrder } from '@/utils/menu/getMenuItemsOrder';

//types
import type { SocialProfileType } from '@/types/socialProfileType';

import { SideBarList } from '@/components/composite/SocialProfiles/SocialProfileGroupMenuMobile/Components/SideBarList';

type SocialProfilesMenuMobileContentProps = Pick<SocialProfilesMenuProps, 'toggleSideBar'> & {
    activeItem?: SocialProfileType;
    setActiveItem: (item: SocialProfileType) => void;
    itemsList: SocialProfileType[];
    variant: 'social-profile' | 'group';
};

export function SocialProfilesMenuMobileContent({
    activeItem,
    setActiveItem,
    itemsList,
    variant = 'social-profile',
    toggleSideBar
}: SocialProfilesMenuMobileContentProps) {
    const { dispatch, globalInfo } = useGlobalInfo();

    const orderedItems: SocialProfileType[] = useMemo(() => {
        return getMenuItemsOrder<SocialProfileType>({
            storageKey:
                globalInfo.group.activeGroup && globalInfo.menuGroupActive
                    ? `ico-sp-group-order-${globalInfo.group.activeGroup.id}`
                    : 'ico-sp-order',
            menuItems: itemsList,
            activeItemId:
                globalInfo.group.activeGroup && !globalInfo.group.activeSocialProfile
                    ? null
                    : activeItem?.id
        });
    }, [
        activeItem?.id,
        globalInfo.group.activeGroup,
        globalInfo.group.activeSocialProfile,
        globalInfo.menuGroupActive,
        itemsList
    ]);

    const updateActive = (item: SocialProfileType) => {
        updateActiveItem<SocialProfileType>({
            storageKey:
                globalInfo.group.activeGroup && globalInfo.menuGroupActive
                    ? `ico-sp-group-order-${globalInfo.group.activeGroup.id}`
                    : 'ico-sp-order',
            itemsList: itemsList,
            itemSelectedId: item.id,
            callback: () => {
                if (variant === 'social-profile' && globalInfo.group.activeGroup) {
                    dispatch({ type: CLEAR_GROUP_INFO });
                }
                setActiveItem(item);
            }
        });
    };
    return (
        <SideBarList
            data={orderedItems}
            toggleSideBar={toggleSideBar}
            updateActiveItem={updateActive}
            variant="social-profile"
            activeItem={activeItem}
        />
    );
}

type SocialProfilesMenuProps = {
    toggleSideBar: () => void;
    hasActiveItem?: boolean;
};

export function SocialProfilesMenuMobile({ toggleSideBar, hasActiveItem = true }) {
    const { activeSocialProfile, setActiveSocialProfile } = useActiveSocialProfile();
    const { activatedSocialProfiles } = useSocialProfiles();

    return (
        <SocialProfilesMenuMobileContent
            variant="social-profile"
            toggleSideBar={toggleSideBar}
            setActiveItem={setActiveSocialProfile}
            itemsList={activatedSocialProfiles}
            activeItem={hasActiveItem ? activeSocialProfile : null}
        />
    );
}
